import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import api, { setUnauthorizedHandler, isTokenExpired } from "../api/client";

interface AuthUser {
  id: string;
  email: string;
  role: string;
  is_admin: boolean;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = "autoval_token";
const USER_KEY = "autoval_user";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = localStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored) : null;
  });
  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem(TOKEN_KEY)
  );
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  // Register the logout handler with the API client so 401 responses
  // trigger React-level state changes instead of a hard window.location
  // redirect (which causes blank screens).
  useEffect(() => {
    setUnauthorizedHandler(logout);
  }, [logout]);

  // Apply a freshly-exchanged session (used by the SSO handoff below).
  const applySession = useCallback((newToken: string, newUser: AuthUser) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  // SSO handoff (Vuswar one-click "Connect to AutoVal"): if the deep link
  // carries `?sso=<token>`, exchange it for a normal session, strip the
  // param from the URL, and continue to the deal — no second login.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("sso");
    if (!ssoToken) return;

    let cancelled = false;
    setIsLoading(true);
    api
      .post("/api/auth/sso-login", { token: ssoToken })
      .then((res) => {
        if (cancelled) return;
        applySession(res.data.token as string, res.data.user as AuthUser);
        // Drop the one-time token from the URL (history + address bar).
        params.delete("sso");
        const qs = params.toString();
        const clean = window.location.pathname + (qs ? `?${qs}` : "");
        window.history.replaceState({}, "", clean);
      })
      .catch(() => {
        if (!cancelled) window.location.replace("/sign-in");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applySession]);

  // Validate token on mount
  useEffect(() => {
    // Defer to the SSO handoff when a handoff token is present.
    if (new URLSearchParams(window.location.search).get("sso")) {
      return;
    }
    if (!token) {
      setIsLoading(false);
      return;
    }

    // Client-side expiry check: if the token is already expired, skip
    // the API call entirely and logout immediately. This prevents the
    // blank screen that occurs when the /api/auth/me call returns 401
    // and races with the interceptor.
    if (isTokenExpired(token)) {
      logout();
      setIsLoading(false);
      return;
    }

    api
      .get("/api/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setUser(res.data);
        localStorage.setItem(USER_KEY, JSON.stringify(res.data));
      })
      .catch(() => {
        logout();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Periodic token refresh — keeps session alive during long operations
  // (e.g., document parsing). Checks expiry every 5 minutes and calls
  // /api/auth/me to stay fresh.
  useEffect(() => {
    if (!token) return;

    const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
    const interval = setInterval(() => {
      // Client-side expiry check before making the call
      const currentToken = localStorage.getItem(TOKEN_KEY);
      if (!currentToken || isTokenExpired(currentToken)) {
        logout();
        return;
      }

      api
        .get("/api/auth/me", {
          headers: { Authorization: `Bearer ${currentToken}` },
        })
        .then((res) => {
          setUser(res.data);
          localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        })
        .catch(() => {
          logout();
        });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [token, logout]);

  const login = useCallback(async (email: string, password: string) => {
    const { data } = await api.post("/api/auth/login", { email, password });
    const newToken = data.token as string;
    const newUser = data.user as AuthUser;

    localStorage.setItem(TOKEN_KEY, newToken);
    localStorage.setItem(USER_KEY, JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated: !!user && !!token,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
